import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Mic, ChevronDown } from "lucide-react";
import { api, fmtDate } from "@/lib/api";

const tone = (s) => (s >= 8 ? "bg-teal-600" : s >= 5 ? "bg-sky-500" : "bg-amber-500");

function Trend({ interviews }) {
  const all = [...interviews].reverse().flatMap((iv) => iv.answers.map((a) => ({ ...a, role: iv.role, date: iv.started_at })));
  if (all.length < 2) return null;
  const firstAvg = interviews[interviews.length - 1].average;
  const lastAvg = interviews[0].average;
  const delta = Math.round((lastAvg - firstAvg) * 10) / 10;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm" data-testid="interview-trend">
        {delta >= 0 ? <TrendingUp className="h-4 w-4 text-teal-600" aria-hidden /> : <TrendingDown className="h-4 w-4 text-amber-600" aria-hidden />}
        <span className="text-slate-700">
          {interviews.length > 1 ? <>Average went from <strong>{firstAvg}</strong> to <strong>{lastAvg}</strong> ({delta >= 0 ? "+" : ""}{delta}) across {interviews.length} interviews</> : <>Average <strong>{lastAvg}</strong>/10 so far</>}
        </span>
      </div>
      <div className="flex items-end gap-1 h-24 border-b border-slate-200" role="img" aria-label={`Answer scores in order: ${all.map((a) => a.score).join(", ")}`} data-testid="interview-score-chart">
        {all.map((a, i) => (
          <div key={i} className="flex-1 max-w-[22px] flex flex-col justify-end h-full" title={`${a.role} · Q${a.q}: ${a.score}/10`}>
            <div className={`${tone(a.score)} rounded-t-sm transition-[height]`} style={{ height: `${Math.max(6, a.score * 10)}%` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function InterviewRow({ iv, i }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border border-slate-200 rounded-xl bg-white" data-testid={`interview-row-${i}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex flex-wrap items-center gap-3 justify-between p-3 text-left">
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-slate-900">{iv.role}</span>
          <span className="block text-xs text-slate-500">{fmtDate(iv.started_at)} · {iv.answers.length}/5 answered{iv.completed ? " · complete" : ""}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="flex gap-1">{iv.answers.map((a) => <span key={a.q} className={`${tone(a.score)} text-white text-[11px] font-bold rounded-md w-6 h-6 flex items-center justify-center`}>{a.score}</span>)}</span>
          <span className="text-sm font-bold text-slate-900 tabular-nums w-14 text-right" data-testid={`interview-avg-${i}`}>{iv.average}/10</span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
        </span>
      </button>
      {open && (
        <ol className="px-3 pb-3 space-y-3">
          {iv.answers.map((a) => (
            <li key={a.q} className="text-sm border-t border-slate-100 pt-3 space-y-1">
              <p className="font-medium text-slate-800">Q{a.q} · {a.score}/10 — {a.question}</p>
              <p className="text-slate-600 whitespace-pre-wrap line-clamp-6">Your answer: {a.answer}</p>
            </li>
          ))}
        </ol>
      )}
    </li>
  );
}

export function InterviewHistory({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/coach/interviews").then((r) => setData(r.data.interviews)).catch(() => setData([])); }, [refreshKey]);
  if (!data) return null;
  if (!data.length) {
    return (
      <p className="text-sm text-slate-600 flex gap-2" data-testid="interview-history-empty">
        <Mic className="h-4 w-4 text-violet-700 shrink-0 mt-0.5" aria-hidden />
        No scored mock interviews yet. Open the Career Coach and choose "Start a mock interview" to begin tracking your scores.
      </p>
    );
  }
  return (
    <div className="space-y-4" data-testid="interview-history">
      <Trend interviews={data} />
      <ul className="space-y-2">{data.map((iv, i) => <InterviewRow key={iv.id} iv={iv} i={i} />)}</ul>
      <p className="text-[11px] text-slate-500">Scores are AI practice feedback, not a prediction of hiring outcomes.</p>
    </div>
  );
}
